import {DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent} from '@dnd-kit/core';
import {SortableContext, arrayMove, horizontalListSortingStrategy} from '@dnd-kit/sortable';
import {Dropdown, DropdownItem, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {VIRTUAL_PRESET_ID} from '../../constants/VIRTUAL_PRESET_ID';
import {Preset} from '../../types/Api';
import {isPresetCustom} from '../../utils/isPresetCustom';
import {ChevronLeftGlyph, ChevronRightGlyph, ChevronDownGlyph} from '../icons/StrokeGlyphs';
import {AddPresetTab} from './components/AddPresetTab/AddPresetTab';
import {PresetActionsMenu} from './components/PresetActionsMenu/PresetActionsMenu';
import {PresetButton} from './components/PresetButton/PresetButton';
import {SortablePresetButton} from './components/SortablePresetButton/SortablePresetButton';
import {PresetApiStateEnum} from './constants/PresetApiStateEnum';
import {usePresetApiState} from './hooks/usePresetApiState';
import {usePresetColorPreview} from './hooks/usePresetColorPreview';
import {usePresetRowCounts} from './hooks/usePresetRowCounts';
import {PresetsPanelRendererProps} from './types/PresetsPanelRendererProps';
import {DRAG_ACTIVATION_DISTANCE, makeVirtualRowCountEntry} from './utils/presetPanelConstants';

const SCROLL_STEP = 200;

const ARROW_BUTTON_STYLE = {
  border: '1px solid #DFE1E6',
  background: '#FFFFFF',
  color: '#42526E',
} as const;

export function PresetTabsScrollRenderer(props: PresetsPanelRendererProps) {
  const t = useTranslationContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const {
    apiState,
    copyState,
    isPresetChangeDisabled,
    updatePresetProxy,
    duplicatePresetProxy,
    deletePresetProxy,
    applyPresetToVirtualProxy,
    updatePresetPositionProxy,
    activatePresetProxy,
  } = usePresetApiState({
    updatePreset: props.updatePreset,
    duplicatePreset: props.duplicatePreset,
    deletePreset: props.deletePreset,
    applyPresetToVirtual: props.applyPresetToVirtual,
    updatePresetPosition: props.updatePresetPosition,
  });

  const activePresetId = (props.activePreset as Preset)?.id;
  const isVirtualActive = isNotNil(props.activePreset) && !isPresetCustom(props.activePreset);
  const virtualState = apiState[VIRTUAL_PRESET_ID] ?? PresetApiStateEnum.idle;

  const rowCountItems = useMemo(() => {
    if (!props.virtualPreset || isVirtualActive) {
      return props.presets;
    }
    return [makeVirtualRowCountEntry(props.virtualPreset), ...(props.presets ?? [])];
  }, [props.presets, props.virtualPreset, isVirtualActive]);

  const rowCounts = usePresetRowCounts({
    visibleItems: rowCountItems,
    rowCountGetter: props.rowCountGetter,
  });
  const presetsRowCount = rowCounts.presetsRowCount;

  // Local order state for drag-and-drop — synced from props.presets
  const [localPresets, setLocalPresets] = useState<Preset[]>(props.presets ?? []);
  const [prevPresets, setPrevPresets] = useState(props.presets);

  if (prevPresets !== props.presets) {
    setPrevPresets(props.presets);
    setLocalPresets(props.presets ?? []);
  }

  const {resolveColor, setPreviewColor} = usePresetColorPreview(props.presets);

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: DRAG_ACTIVATION_DISTANCE}})
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (isPresetChangeDisabled) {
      return;
    }
    const {active, over} = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = localPresets.findIndex((p) => p.id === active.id);
    const newIndex = localPresets.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    setLocalPresets((prev) => arrayMove(prev, oldIndex, newIndex));
    updatePresetPositionProxy(active.id as string, oldIndex, newIndex);
  };

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  // Detect overflow on mount and whenever content resizes
  // eslint-disable-next-line eslint-js/no-restricted-syntax -- cleanup-only useEffect with stable deps
  useEffect(
    function observeScrollAreaResize() {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      const observer = new ResizeObserver(() => {
        window.requestAnimationFrame(updateScrollState);
      });
      observer.observe(el);
      // Also observe children count changes by observing the container itself
      updateScrollState();
      return () => observer.disconnect();
    },
    [updateScrollState]
  );

  const scrollBy = useCallback((delta: number) => {
    scrollRef.current?.scrollBy({left: delta, behavior: 'smooth'});
    // Arrow visibility is updated continuously by the onScroll handler during the animation.
  }, []);

  const renderPresetButton = (preset: Preset) => {
    const isActive = preset.id === activePresetId;
    const state = apiState[preset.id] ?? PresetApiStateEnum.idle;
    return (
      <SortablePresetButton
        sortableId={preset.id}
        color={resolveColor(preset.id, preset.color)}
        rowCount={presetsRowCount[preset.dataQueryId]}
        unsaved={props.isPresetChanged && isActive}
        key={preset.id}
        preset={preset}
        state={state}
        copyIsLoading={copyState[preset.id] === PresetApiStateEnum.loading}
        active={isActive}
        isDisabled={isPresetChangeDisabled && state !== PresetApiStateEnum.loading}
        onClick={() => {
          if (isActive || isPresetChangeDisabled || state === PresetApiStateEnum.loading) {
            return;
          }
          activatePresetProxy(preset.id, () => props.activatePreset(preset));
        }}
        data-testid={suffixTestId(`preset-${preset.title}`, props)}
        actions={(closeMenu) => (
          <PresetActionsMenu
            preset={preset}
            presetChanged={props.isPresetChanged}
            activePresetId={activePresetId}
            presetColor={resolveColor(preset.id, preset.color)}
            onColorChange={setPreviewColor}
            updatePreset={updatePresetProxy}
            deletePreset={deletePresetProxy}
            duplicatePreset={duplicatePresetProxy}
            applyPresetToVirtual={applyPresetToVirtualProxy}
            applyToDefaultState={copyState[preset.id]}
            onCopySuccess={closeMenu}
            applyToDefaultDisabled={isPresetChangeDisabled}
            resetOverrides={props.resetOverrides}
            renamePreset={props.renamePreset}
            testId={suffixTestId(`preset-${preset.title}`, props)}
            editDialogTestId={suffixTestId('editPreset', props)}
            dialogOptionsTestId={suffixTestId('editPreset', props)}
            dialogId="editPreset"
          />
        )}
      />
    );
  };

  return (
    <div className="flex items-center w-full min-w-0 gap-[2px]">
      {/* Left scroll arrow — only visible when there's overflow to the left */}
      <Show when={canScrollLeft}>
        <button
          type="button"
          aria-label={t('general.presets.scrollLeft')}
          onClick={() => scrollBy(-SCROLL_STEP)}
          style={ARROW_BUTTON_STYLE}
          className="flex-shrink-0 inline-flex items-center justify-center w-[30px] h-8 rounded-[4px] cursor-pointer"
        >
          <ChevronLeftGlyph />
        </button>
      </Show>

      {/* Scrollable single-line strip */}
      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className="flex items-center gap-x-[6px] flex-1 min-w-0"
        style={{overflowX: 'auto', scrollbarWidth: 'none'}}
      >
        {/* Pinned virtual-preset ("Default") tab */}
        <Show when={props.virtualPreset}>
          {(virtualPreset) => (
            <PresetButton
              rowCount={
                isVirtualActive
                  ? props.activeViewRowCount
                  : presetsRowCount[virtualPreset.dataQueryId]
              }
              unsaved={false}
              preset={{
                id: VIRTUAL_PRESET_ID,
                title: t('page.datagrid.labels.defaultView'),
                description: '',
                dataQueryId: virtualPreset.dataQueryId,
                isActive: isVirtualActive,
              }}
              state={virtualState}
              active={isVirtualActive}
              isDisabled={isPresetChangeDisabled && virtualState !== PresetApiStateEnum.loading}
              onClick={() => {
                if (
                  isVirtualActive ||
                  isPresetChangeDisabled ||
                  virtualState === PresetApiStateEnum.loading
                ) {
                  return;
                }
                activatePresetProxy(VIRTUAL_PRESET_ID, () => props.deactivatePreset());
              }}
              data-testid={suffixTestId('preset-default', props)}
            />
          )}
        </Show>

        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext
            items={localPresets.map((p) => p.id)}
            strategy={horizontalListSortingStrategy}
          >
            {localPresets.map(renderPresetButton)}
          </SortableContext>
        </DndContext>

        <AddPresetTab
          isDisabled={isPresetChangeDisabled}
          onCreate={props.createPreset}
          data-testid={props['data-testid']}
        />
      </div>

      {/* Right scroll arrow — only visible when there's overflow to the right */}
      <Show when={canScrollRight}>
        <button
          type="button"
          aria-label={t('general.presets.scrollRight')}
          onClick={() => scrollBy(SCROLL_STEP)}
          style={ARROW_BUTTON_STYLE}
          className="flex-shrink-0 inline-flex items-center justify-center w-[30px] h-8 rounded-[4px] cursor-pointer"
        >
          <ChevronRightGlyph />
        </button>
      </Show>

      {/* All-presets dropdown — always visible, shows total count */}
      <Dropdown
        isLazy
        strategy="fixed"
        dropdownControl={
          <button
            type="button"
            data-testid={suffixTestId('allPresetsDropdown', props)}
            aria-label={t('general.presets.morePresets')}
            style={{...ARROW_BUTTON_STYLE, paddingLeft: 9, paddingRight: 8}}
            className="flex-shrink-0 inline-flex items-center gap-1 h-8 ml-1 rounded-[4px] cursor-pointer"
          >
            <ChevronDownGlyph />
            <span className="text-[11px] font-bold leading-none bg-[#EBECF0] text-[#42526E] rounded-full px-[6px] py-px tabular-nums">
              {localPresets.length}
            </span>
          </button>
        }
        data-testid={suffixTestId('allPresetsMenu', props)}
      >
        {localPresets.map((preset) => {
          const isActive = preset.id === activePresetId;
          const color = resolveColor(preset.id, preset.color);
          return (
            <DropdownItem
              key={preset.id}
              isActive={isActive}
              isDisabled={isPresetChangeDisabled}
              label={preset.title}
              prefix={
                <span
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: color || 'transparent',
                    flexShrink: 0,
                  }}
                />
              }
              suffix={
                presetsRowCount[preset.dataQueryId] !== undefined ? (
                  <span className="text-[11px] font-semibold text-[#6B778C] tabular-nums">
                    {presetsRowCount[preset.dataQueryId]}
                  </span>
                ) : undefined
              }
              onClick={() => {
                if (isActive || isPresetChangeDisabled) {
                  return;
                }
                activatePresetProxy(preset.id, () => props.activatePreset(preset));
              }}
              data-testid={suffixTestId(`allPresets-${preset.title}`, props)}
            />
          );
        })}
      </Dropdown>
    </div>
  );
}
